import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { db, storage } from "@lib/firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { Button } from "@cakrawalajakarta/shared-components";

const fieldClass =
  "input w-full h-10 px-3 border border-gray-300 rounded-xl focus:ring-0 bg-white text-gray-900 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-500";

const selectClass =
  "input w-full h-10 py-1 px-3 border border-gray-300 rounded-xl focus:ring-0 bg-white text-gray-900 text-sm font-medium disabled:bg-gray-100 disabled:text-gray-500";

const Field = ({ label, name, type = "text", value, onChange, required, placeholder, disabled }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      disabled={disabled}
      className={fieldClass}
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, required, disabled, placeholder }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      className={selectClass}
    >
      <option value="">{placeholder || "Pilih..."}</option>
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value;
        const lbl = typeof opt === "string" ? opt : opt.label;
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  </div>
);

// ─── Kategori Helper ─────────────────────────────────────────────────────────
const isFunRunCategory = (categoryName = "") =>
  categoryName.toLowerCase().includes("fun run");

const isRelayCategory = (categoryName = "") =>
  categoryName.toLowerCase().includes("relay");

const requiresQualification = (categoryName = "") =>
  !isFunRunCategory(categoryName) && !isRelayCategory(categoryName);

const createEmptyRelayMember = () => ({
  namaLengkap: "",
  bibName: "",
  telepon: "",
  nomorIdentitas: "",
  tanggalLahir: "",
  golonganDarah: "",
  sizeJersey: "",
  gender: "",
  negara: "Indonesia",
  provinsi: "",
  kota: "",
  alamat: "",
  kontakDarurat: "",
  namaKontakDarurat: "",
});

const relayRequiredFields = [
  ["namaLengkap", "Nama Lengkap"],
  ["bibName", "BIB Name"],
  ["telepon", "Nomor Telepon"],
  ["nomorIdentitas", "Nomor Identitas"],
  ["tanggalLahir", "Tanggal Lahir"],
  ["golonganDarah", "Golongan Darah"],
  ["sizeJersey", "Ukuran Jersey"],
  ["gender", "Jenis Kelamin"],
  ["negara", "Negara"],
  ["alamat", "Alamat Lengkap"],
  ["kontakDarurat", "Nomor Kontak Darurat"],
  ["namaKontakDarurat", "Nama Kontak Darurat"],
  ["provinsi", "Provinsi"],
];

const sanitizeRelayMember = (member = {}) =>
  Object.keys(createEmptyRelayMember()).reduce((acc, key) => {
    acc[key] = typeof member[key] === "string" ? member[key].trim() : member[key] || "";
    return acc;
  }, {});

// ─── Relay Member Fields ──────────────────────────────────────────────────────
const RelayMemberSection = ({ members, onChange, onAdd, onRemove, countries }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
          Data Anggota Tim
        </p>
        <span className="text-xs text-slate-400">{members.length}/4 anggota</span>
      </div>

      {members.map((member, idx) => (
        <div key={idx}>
          {idx > 0 && <div className="border-t border-slate-100 mb-4" />}
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Peserta {idx + 2}
            </p>
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className="text-xs text-slate-400 hover:text-red-600 transition-colors"
            >
              Hapus
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nama Lengkap" value={member.namaLengkap || ""} onChange={(e) => onChange(idx, "namaLengkap", e.target.value)} required placeholder={`Nama lengkap peserta ${idx + 2}`} />
            <Field label="BIB Name" value={member.bibName || ""} onChange={(e) => onChange(idx, "bibName", e.target.value)} required placeholder="Nama pada BIB" />
            <Field label="Nomor Telepon" value={member.telepon || ""} onChange={(e) => onChange(idx, "telepon", e.target.value)} required placeholder="Nomor WhatsApp aktif" />
            <Field label="Nomor Identitas (KTP/Paspor)" value={member.nomorIdentitas || ""} onChange={(e) => onChange(idx, "nomorIdentitas", e.target.value)} required placeholder="Nomor KTP atau Paspor" />
            <Field label="Tanggal Lahir" type="date" value={member.tanggalLahir || ""} onChange={(e) => onChange(idx, "tanggalLahir", e.target.value)} required />
            <SelectField label="Golongan Darah" value={member.golonganDarah || ""} onChange={(e) => onChange(idx, "golonganDarah", e.target.value)} options={["A", "B", "AB", "O"]} required />
            <SelectField label="Ukuran Jersey" value={member.sizeJersey || ""} onChange={(e) => onChange(idx, "sizeJersey", e.target.value)} options={["XS", "S", "M", "L", "XL", "XXL"]} required />
            <SelectField label="Jenis Kelamin" value={member.gender || ""} onChange={(e) => onChange(idx, "gender", e.target.value)} options={["Laki-laki", "Perempuan"]} required />
            <SelectField label="Negara" value={member.negara || ""} onChange={(e) => onChange(idx, "negara", e.target.value)} options={countries} required placeholder="Pilih Negara" />
            <Field label="Provinsi" value={member.provinsi || ""} onChange={(e) => onChange(idx, "provinsi", e.target.value)} required placeholder="Provinsi / state" />
            <Field label="Kota" value={member.kota || ""} onChange={(e) => onChange(idx, "kota", e.target.value)} placeholder="Kota" />
            <Field label="Alamat Lengkap" value={member.alamat || ""} onChange={(e) => onChange(idx, "alamat", e.target.value)} required />
            <Field label="Nomor Kontak Darurat" value={member.kontakDarurat || ""} onChange={(e) => onChange(idx, "kontakDarurat", e.target.value)} required />
            <Field label="Nama Kontak Darurat" value={member.namaKontakDarurat || ""} onChange={(e) => onChange(idx, "namaKontakDarurat", e.target.value)} required />
          </div>
        </div>
      ))}

      {members.length < 4 && (
        <button
          type="button"
          onClick={onAdd}
          className="w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-dashed border-slate-300 text-slate-400 text-sm hover:border-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Tambah Anggota Tim
        </button>
      )}
    </div>
  );
};

// ─── Main Form Component ──────────────────────────────────────────────────────
const InlineFormContent = ({
  event,
  selectedCategory,
  collectionName,
  isSnapReady,
  onRegistrationComplete,
}) => {
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const { showNotification } = useNotification();

  const categoryName = selectedCategory?.categoryName || "";
  const isFunRun = isFunRunCategory(categoryName);
  const isRelay = isRelayCategory(categoryName);
  const needsQualification = requiresQualification(categoryName);

  const [form, setForm] = useState({
    namaLengkap: "",
    bibName: "",
    telepon: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    golonganDarah: "",
    sizeJersey: "",
    gender: "",
    negara: "Indonesia",
    provinsi: "",
    kota: "",
    alamat: "",
    kontakDarurat: "",
    namaKontakDarurat: "",
    raceResultLink: "",
    certificateUrl: "",
  });

  const [relayMembers, setRelayMembers] = useState([]);

  const [isChecked, setIsChecked] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [uploadingCertificate, setUploadingCertificate] = useState(false);

  const GEO_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/geo`;
  const cache = useMemo(() => new Map(), []);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [geoLoading, setGeoLoading] = useState({ countries: false, provinces: false, cities: false });

  const fetchWithCache = useCallback(async (url, cacheKey, loadingKey) => {
    if (cache.has(cacheKey)) return cache.get(cacheKey);
    setGeoLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      cache.set(cacheKey, data);
      return data;
    } catch {
      return null;
    } finally {
      setGeoLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }, [cache]);

  useEffect(() => {
    fetchWithCache(
      `${GEO_API_BASE}/countries`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const list = data.geonames
          .map((c) => ({ value: c.countryName, label: c.countryName }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setCountries(list);
        setRawCountries(data.geonames);
      }
    });
  }, [fetchWithCache]);

  useEffect(() => {
    if (form.negara && rawCountries.length > 0) {
      setProvinces([]);
      setCities([]);
      setForm((prev) => ({ ...prev, provinsi: "", kota: "" }));
      const selected = rawCountries.find((c) => c.countryName === form.negara);
      if (!selected) return;
      fetchWithCache(
        `${GEO_API_BASE}/children/${selected.geonameId}?lang=en`,
        `provinces_${selected.geonameId}`,
        "provinces"
      ).then((data) => {
        if (data?.geonames) {
          const list = data.geonames.map((p) => ({ value: p.name, label: p.name }));
          const idMap = {};
          data.geonames.forEach((p) => { idMap[p.name] = p.geonameId; });
          setProvinces(list);
          setProvinceIds(idMap);
        }
      });
    }
  }, [form.negara, rawCountries, fetchWithCache]);

  useEffect(() => {
    if (form.provinsi && provinceIds[form.provinsi]) {
      setCities([]);
      setForm((prev) => ({ ...prev, kota: "" }));
      fetchWithCache(
        `${GEO_API_BASE}/children/${provinceIds[form.provinsi]}?lang=en`,
        `cities_${provinceIds[form.provinsi]}`,
        "cities"
      ).then((data) => {
        if (data?.geonames) {
          const list = data.geonames.map((c) => ({ value: c.name, label: c.name }));
          setCities(list);
        }
      });
    }
  }, [form.provinsi, provinceIds, fetchWithCache]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!snap.exists()) return;
        const d = snap.data();
        setForm((prev) => ({
          ...prev,
          namaLengkap: d.namaLengkap || `${d.namaDepan || ""} ${d.namaBelakang || ""}`.trim() || "",
          telepon: d.telepon || "",
          nomorIdentitas: d.nomorIdentitas || "",
          tanggalLahir: d.tanggalLahir || "",
          golonganDarah: d.golonganDarah || "",
          sizeJersey: d.sizeJersey || "",
          gender: d.gender || "",
          negara: d.negara || "Indonesia",
          provinsi: d.provinsi || "",
          kota: d.kota || "",
          alamat: d.alamat || "",
          kontakDarurat: d.kontakDarurat || "",
          namaKontakDarurat: d.namaKontakDarurat || "",
          bibName: d.bibName || "",
          raceResultLink: d.raceResultLink || "",
          certificateUrl: d.certificateUrl || "",
        }));
      } catch {}
    })();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRelayMemberChange = (idx, field, value) => {
    setRelayMembers((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleRelayMemberAdd = () => {
    if (relayMembers.length >= 4) return;
    setRelayMembers((prev) => [...prev, createEmptyRelayMember()]);
  };

  const handleRelayMemberRemove = (idx) => {
    setRelayMembers((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCertificateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showNotification("error", "Ukuran file terlalu besar (maks 5MB).");
      return;
    }
    setUploadingCertificate(true);
    try {
      const userId = user?.uid;
      const timestamp = Date.now();
      const fileName = `cert_${userId}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `qualifications/${userId}/${fileName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setForm((prev) => ({ ...prev, certificateUrl: url }));
      showNotification("success", "Sertifikat berhasil diunggah!");
    } catch (err) {
      console.error("Upload error:", err);
      showNotification("error", "Gagal mengunggah sertifikat. Silakan coba lagi.");
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleSubmit = async () => {
    if (loading) return;

    if (!user) {
      showNotification("error", "Silakan login terlebih dahulu.");
      navigate("/login");
      return;
    }

    if (!isChecked) {
      showNotification("error", "Harap setujui Waiver terlebih dahulu.");
      return;
    }

    const required = [
      ["namaLengkap", "Nama Lengkap"],
      ["bibName", "BIB Name"],
      ["telepon", "Nomor Telepon"],
      ["nomorIdentitas", "Nomor Identitas"],
      ["tanggalLahir", "Tanggal Lahir"],
      ["golonganDarah", "Golongan Darah"],
      ["sizeJersey", "Ukuran Jersey"],
      ["gender", "Jenis Kelamin"],
      ["negara", "Negara"],
      ["alamat", "Alamat"],
      ["kontakDarurat", "Kontak Darurat"],
      ["namaKontakDarurat", "Nama Kontak Darurat"],
    ];

    if (needsQualification) {
      required.push(["raceResultLink", "Tautan Strava / Hasil Lomba"]);
    }

    for (const [field, label] of required) {
      if (!form[field] || form[field].trim() === "") {
        showNotification("error", `${label} wajib diisi.`);
        return;
      }
    }

    if (needsQualification && form.raceResultLink) {
      const trimmed = form.raceResultLink.trim();
      const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      if (!/^https?:\/\/[^\s]+/.test(normalized)) {
        showNotification("error", "Tautan Strava tidak valid. Pastikan diawali dengan https://");
        return;
      }
    }

    if (isRelay) {
      if (relayMembers.length < 4) {
        showNotification("error", `Data anggota tim belum lengkap. Tambahkan ${4 - relayMembers.length} anggota lagi.`);
        return;
      }
      for (let i = 0; i < relayMembers.length; i++) {
        const m = relayMembers[i] || {};
        for (const [field, label] of relayRequiredFields) {
          if (!m[field] || String(m[field]).trim() === "") {
            showNotification("error", `${label} Peserta ${i + 2} wajib diisi.`);
            return;
          }
        }
      }
    }

    setLoading(true);
    setErrorMsg("");

    try {
      await updateDoc(doc(db, "users", user.uid), {
        namaLengkap: form.namaLengkap.trim(),
        telepon: form.telepon.trim(),
        nomorIdentitas: form.nomorIdentitas.trim(),
        tanggalLahir: form.tanggalLahir.trim(),
        gender: form.gender.trim(),
        negara: form.negara.trim(),
        provinsi: form.provinsi?.trim() || "",
        kota: form.kota?.trim() || "",
        alamat: form.alamat.trim(),
        kontakDarurat: form.kontakDarurat.trim(),
        namaKontakDarurat: form.namaKontakDarurat.trim(),
        bibName: form.bibName.trim(),
        golonganDarah: form.golonganDarah.trim(),
        sizeJersey: form.sizeJersey.trim(),
        ...(needsQualification ? { raceResultLink: form.raceResultLink.trim() } : {}),
        ...(needsQualification && form.certificateUrl ? { certificateUrl: form.certificateUrl } : {}),
        updatedAt: serverTimestamp(),
      });

      const price = selectedCategory.price ?? event.price ?? 0;
      const foreignerPrice = selectedCategory.foreignerPrice ?? price;
      const finalPrice =
        form.negara === "Indonesia" ? price : foreignerPrice;

      const payload = {
        namaLengkap: form.namaLengkap.trim(),
        email: user.email || "",
        telepon: form.telepon.trim(),
        nomorIdentitas: form.nomorIdentitas.trim(),
        tanggalLahir: form.tanggalLahir.trim(),
        gender: form.gender.trim(),
        negara: form.negara.trim(),
        provinsi: form.provinsi?.trim() || "",
        kota: form.kota?.trim() || "",
        alamat: form.alamat.trim(),
        kontakDarurat: form.kontakDarurat.trim(),
        namaKontakDarurat: form.namaKontakDarurat.trim(),
        bibName: form.bibName.trim(),
        golonganDarah: form.golonganDarah.trim(),
        sizeJersey: form.sizeJersey.trim(),
        kategori: selectedCategory.categoryName,
        distance: selectedCategory.distance || 0,
        harga: Math.floor(finalPrice),
        eventId: event.id,
        uid: user.uid,
        collectionName,
        ...(needsQualification ? {
          raceResultLink: form.raceResultLink.trim(),
          certificateUrl: form.certificateUrl || null,
        } : {}),
        ...(isRelay ? {
          relayMembers: relayMembers.map(sanitizeRelayMember),
        } : {}),
      };

      const token = await getIdToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/register`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { snapToken, orderId } = response.data || {};

      if (snapToken) {
        if (window.snap) {
          window.snap.pay(snapToken, {
            onSuccess: () => {
              showNotification("success", "Pembayaran berhasil!");
              onRegistrationComplete?.({
                orderId,
                transactionStatus: "settlement",
                snapToken,
              });
            },
            onPending: () => {
              showNotification("info", "Pembayaran sedang diproses.");
              onRegistrationComplete?.({
                orderId,
                transactionStatus: "",
                snapToken,
              });
            },
            onError: () => {
              showNotification("error", "Pembayaran gagal. Silakan coba lagi.");
            },
            onClose: () => {
              onRegistrationComplete?.({
                orderId,
                transactionStatus: "",
                snapToken,
              });
            },
          });
        } else {
          onRegistrationComplete?.({
            orderId,
            transactionStatus: "",
            snapToken,
          });
        }
      } else {
        if (String(event?.id || "").toUpperCase() === "ULTRA24") {
          navigate("/MyRegistration");
        } else {
          onRegistrationComplete?.({
            orderId,
            transactionStatus: "",
            snapToken: null,
          });
        }
      }
    } catch (error) {
      const existingOrderId = error?.response?.data?.orderId;
      if (error?.response?.status === 409 && existingOrderId) {
        showNotification("info", "Pendaftaran untuk event ini sudah ada. Membuka detail pesanan Anda.");
        onRegistrationComplete?.({
          orderId: existingOrderId,
          transactionStatus: error?.response?.data?.transactionStatus || "pending",
          snapToken: error?.response?.data?.snapToken || null,
        });
        setErrorMsg("");
        return;
      }

      const msg =
        error.response?.data?.message ||
        error.message ||
        "Pendaftaran gagal. Silakan coba lagi.";
      setErrorMsg(msg);
      showNotification("error", msg);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
        <p className="text-slate-600 mb-4">
          Silakan login untuk mendaftar.
        </p>
        <Button onClick={() => navigate("/login")} variant="primary">
          Login Sekarang
        </Button>
      </div>
    );
  }

  return (
    <>
      {waiverOpen && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setWaiverOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-slate-900">Surat Pernyataan &amp; Waiver</h3>
              <button
                onClick={() => setWaiverOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">
              <p className="mb-4">
                Saya menyatakan telah membaca dan menyetujui keseluruhan pernyataan dalam dokumen ini, maka secara sadar Saya bertanggung jawab atas segala resiko yang terjadi selama dan setelah perlombaan berlangsung.
              </p>
              <p className="mb-4">
                Sehubungan dengan kepesertaan Saya di <strong>ULTRA24 JAKARTA</strong>, Saya secara sadar menyetujui pelepasan tanggung jawab atas segala resiko yang terjadi.
              </p>
              <ol className="list-decimal list-inside space-y-3">
                <li>Saya telah membaca dan menyetujui segala informasi terkait acara, peraturan serta informasi lainnya yang berkaitan dengan <strong>ULTRA24 JAKARTA</strong>.</li>
                <li>Saya menyatakan dalam keadaan sehat secara fisik untuk mengikuti lomba <strong>ULTRA24 JAKARTA</strong>.</li>
                <li>Saya menyatakan, segala bentuk cedera, penyakit atau cacat fisik yang terjadi sebelum dan/atau sesudah lomba berlangsung, dimana Saya mengakui cedera tersebut menjadi tanggung jawab Saya.</li>
                <li>Saya menyadari bahwa lomba lari <strong>ULTRA24 JAKARTA</strong> berpotensi secara fisik menyebabkan saya cedera atau meninggal dunia selama lomba berlangsung. Jika Saya mengetahui adanya gangguan medis yang dapat mengganggu Saya saat lomba, maka Saya akan mengundurkan diri untuk mencegah bahaya cedera atau kondisi medis lainnya.</li>
                <li>Saya menyatakan berpartisipasi pada lomba <strong>ULTRA24 JAKARTA</strong> dengan pertimbangan matang dan atas kemauan sendiri.</li>
                <li>Saya setuju untuk mematuhi semua peraturan dan regulasi yang ditetapkan oleh panitia penyelenggara.</li>
                <li>Saya memberikan izin kepada panitia untuk menggunakan foto dan video Saya selama acara untuk keperluan promosi.</li>
              </ol>
            </div>
            <div className="mt-6">
              <Button onClick={() => setWaiverOpen(false)} variant="primary" className="w-full">
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            Daftar Sekarang
          </p>
          <p className="text-sm text-slate-600 mt-1">
            Mendaftar untuk{" "}
            <strong>{event.eventName}</strong> –{" "}
            <strong>{selectedCategory.categoryName}</strong>
          </p>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Nama Lengkap"
              name="namaLengkap"
              value={form.namaLengkap}
              onChange={handleChange}
              required
            />
            <Field
              label="BIB Name"
              name="bibName"
              value={form.bibName}
              onChange={handleChange}
              required
            />
            <Field
              label="Nomor Telepon"
              name="telepon"
              value={form.telepon}
              onChange={handleChange}
              required
            />
            <Field
              label="Nomor Identitas (KTP/Paspor)"
              name="nomorIdentitas"
              value={form.nomorIdentitas}
              onChange={handleChange}
              required
            />
            <Field
              label="Tanggal Lahir"
              name="tanggalLahir"
              type="date"
              value={form.tanggalLahir}
              onChange={handleChange}
              required
            />
            <SelectField
              label="Golongan Darah"
              name="golonganDarah"
              value={form.golonganDarah}
              onChange={handleChange}
              options={["A", "B", "AB", "O"]}
              required
            />
            <SelectField
              label="Ukuran Jersey"
              name="sizeJersey"
              value={form.sizeJersey}
              onChange={handleChange}
              options={["XS", "S", "M", "L", "XL", "XXL"]}
              required
            />
            <SelectField
              label="Jenis Kelamin"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              options={["Laki-laki", "Perempuan"]}
              required
            />
            <SelectField
              label="Negara"
              name="negara"
              value={form.negara}
              onChange={handleChange}
              options={countries}
              required
              disabled={geoLoading.countries}
              placeholder={geoLoading.countries ? "Memuat negara..." : "Pilih Negara"}
            />
            <SelectField
              label="Provinsi"
              name="provinsi"
              value={form.provinsi}
              onChange={handleChange}
              options={provinces}
              disabled={!form.negara || geoLoading.provinces}
              required={provinces.length > 0}
              placeholder={geoLoading.provinces ? "Memuat provinsi..." : provinces.length > 0 ? "Pilih Provinsi" : "Tidak tersedia"}
            />
            <SelectField
              label="Kota"
              name="kota"
              value={form.kota}
              onChange={handleChange}
              options={cities}
              disabled={!form.provinsi || geoLoading.cities}
              required={cities.length > 0}
              placeholder={geoLoading.cities ? "Memuat kota..." : cities.length > 0 ? "Pilih Kota" : "Tidak tersedia"}
            />
            <Field
              label="Alamat Lengkap"
              name="alamat"
              value={form.alamat}
              onChange={handleChange}
              required
            />
            <Field
              label="Nomor Kontak Darurat"
              name="kontakDarurat"
              value={form.kontakDarurat}
              onChange={handleChange}
              required
            />
            <Field
              label="Nama Kontak Darurat"
              name="namaKontakDarurat"
              value={form.namaKontakDarurat}
              onChange={handleChange}
              required
            />

            {needsQualification && (
              <>
                <Field
                  label="Tautan Strava / Hasil Lomba (Bukti Kualifikasi)"
                  name="raceResultLink"
                  value={form.raceResultLink}
                  onChange={handleChange}
                  required
                  placeholder="Contoh: https://www.strava.com/activities/12345678"
                />
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Unggah Sertifikat / Finisher Cert
                    <span className="ml-1 text-slate-400 font-normal">(Opsional, Maks. 5MB)</span>
                  </label>
                  <div className="w-full h-10 flex items-center px-3 border border-gray-300 rounded-xl bg-white gap-3">
                    <label
                      htmlFor="cert-upload"
                      className={`cursor-pointer text-xs font-semibold px-2.5 py-1 rounded-lg border ${
                        uploadingCertificate
                          ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                          : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 transition-colors"
                      }`}
                    >
                      {uploadingCertificate ? "Mengunggah..." : "Pilih File"}
                    </label>
                    <input
                      id="cert-upload"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleCertificateUpload}
                      disabled={uploadingCertificate}
                      className="hidden"
                    />
                    <span className="text-sm text-slate-400 truncate flex-1">
                      {form.certificateUrl ? (
                        <a
                          href={form.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 font-medium hover:underline"
                        >
                          Sertifikat tersimpan — Lihat
                        </a>
                      ) : "Belum ada file dipilih"}
                    </span>
                  </div>
                </div>
              </>
            )}

          </div>

          {/* ── Section Anggota Relay ── */}
          {isRelay && (
            <RelayMemberSection
              members={relayMembers}
              onChange={handleRelayMemberChange}
              onAdd={handleRelayMemberAdd}
              onRemove={handleRelayMemberRemove}
              countries={countries}
            />
          )}

          {/* Waiver */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-red-600 flex-shrink-0"
              />
              <span className="text-sm text-slate-700 leading-relaxed">
                Saya menyetujui{" "}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setWaiverOpen(true); }}
                  className="font-semibold text-red-600 underline hover:text-red-700"
                >
                  Surat Pernyataan &amp; Waiver
                </button>{" "}
                dan memahami risiko kegiatan ini.
              </span>
            </label>
          </div>

          {errorMsg && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {errorMsg}
            </div>
          )}

          <div>
            <Button
              onClick={handleSubmit}
              variant="primary"
              disabled={loading || !isChecked}
              className="w-full uppercase"
            >
              {loading ? "Memproses..." : "SUBMIT REGISTRATION"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export const InlineRegistrationForm = (props) => <InlineFormContent {...props} />;
